import axios from "axios";
import router from "../router/index";
import store from "../store";
import { AUTH_LOGOUT, AUTH_REQUEST_REFRESH_TOKEN } from "@/store/actions/auth";
import snackbar from "../utils/snackbar";

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API_URL,
});

//Refresh TOKEN
service.interceptors.response.use(
  (response) => {
    // Return a successful response back to the calling service
    return response;
  },
  (error) => {
    // Return any error which is not due to authentication back to the calling service

    if (error.code === "ERR_CANCELED") {
      return new Promise((resolve, reject) => {
        reject(error);
      });
    }

    if (error.response.status !== 401) {
      if (
        error.config.hasOwnProperty("errorHandle") &&
        error.config.errorHandle === false
      ) {
        return new Promise((resolve, reject) => {
          reject(error);
        });
      }
      // Check for errorHandle config
      if (error.response.status === 403) {
        return new Promise((resolve, reject) => {
          if (
            !(
              error.config.hasOwnProperty("suppressSnackbar") &&
              error.config.suppressSnackbar === true
            )
          ) {
            snackbar.error("Insufficient rights");
          }
          reject(error);
        });
      }

      if (error.response.status === 502) {
        return new Promise((resolve, reject) => {
          snackbar.error();
          reject(error);
        });
      }

      if (error.response.status === 503) {
        return new Promise((resolve, reject) => {
          router.push({ name: "underMaintenance" });
          reject(error);
        });
      }

      // If an error has the response, show the error
      if (error.response) {
        return new Promise((resolve, reject) => {
          if (typeof error.response.data.error_message !== "undefined") {
            Array.isArray(error.response.data.error_message)
              ? snackbar.error(error.response.data.error_message[0])
              : snackbar.error(error.response.data.error_message);
          } else if (typeof error.response.data.error !== "undefined") {
            Array.isArray(error.response.data.error)
              ? snackbar.error(error.response.data.error[0])
              : snackbar.error(error.response.data.error);
          } else {
            snackbar.error();
          }
          reject(error);
        });
      }
    }

    // Try request again with new token
    return store
      .dispatch(AUTH_REQUEST_REFRESH_TOKEN)
      .then((token) => {
        // New request with new token
        const config = error.config;

        config.headers["Authorization"] = `Bearer ${token.data.access_token}`;
        return new Promise((resolve, reject) => {
          axios
            .request(config)
            .then((response) => {
              resolve(response);
            })
            .catch((error) => {
              reject(error);
            });
        });
      })
      .catch((error) => {
        Promise.reject(error);
      });
  },
);

export default {
  service: service,
  //AUTH
  healthcheck: function (params) {
    return service.get("/healthcheck/");
  },
  oauth2: function (data) {
    let postData = `grant_type=password&client_id=${
      store.getters.getClientIDs.clientId
    }&client_secret=${
      store.getters.getClientIDs.clientSecret
    }&username=${encodeURIComponent(
      data.username,
    )}&password=${encodeURIComponent(data.password)}`;

    return service.post("/oauth2/access_token", postData);
  },

  oauth2RefreshToken: function (data) {
    let postData = `grant_type=refresh_token&client_id=${
      store.getters.getClientIDs.clientId
    }&client_secret=${
      store.getters.getClientIDs.clientSecret
    }&refresh_token=${encodeURIComponent(data.refresh_token)}`;

    return service.post("/oauth2/access_token", postData);
  },

  expireUserToken: function () {
    return service.post("/users/me/logout/");
  },

  acceptTOS: function () {
    return service.post("users/accept-tos/", {});
  },

  authIframeUser: function (data) {
    return service.post("crm/jwt-login/", data);
  },

  getCrmUser: function () {
    return service.get("crm/users/me/");
  },

  getCrmLicence: function (dealershipUuid = null, first = true) {
    return service.get("/crm/partners/" + dealershipUuid + "/external-info/");
  },

  getKnowledgeBase: function () {
    return service.get("crm/knowledgebase/");
  },

  requestPasswordReset: function (email) {
    return service.post("users/reset-password/", {
      ...email,
      client_id: store.getters.getClientIDs.clientId,
    });
  },

  register: function (credentials, uuid) {
    const data = {
      ...credentials,
      client_id: store.getters.getClientIDs.clientId,
    };

    return service.post("user/register/?registration_token=" + uuid, data);
  },

  getResetTokenValidity: function (uuid, error = true) {
    var path = "users/reset-password/" + uuid + "/";
    return service.get(path, {
      errorHandle: error,
    });
  },

  resetPassword: function (uuid, password, error = true) {
    var path = "users/reset-password/" + uuid + "/";

    return service.post(
      path,
      {
        password: password,
        client_id: store.getters.getClientIDs.clientId,
      },
      {
        errorHandle: error,
      },
    );
  },

  setPreferredLanguage: function (data) {
    return service.post("users/me/preferred-language/", data);
  },

  getUsernameByUuid: function (uuid, error = true) {
    return service.get("invites/" + uuid + "/register/", {
      errorHandle: error,
    });
  },

  acceptInviteUser: (uuid) => {
    var path = "/crm/invites/" + uuid + "/accept/";
    return service.post(path);
  },

  rejectInviteUser: (uuid) => {
    var path = "/crm/invites/" + uuid + "/reject/";
    return service.post(path);
  },

  setUserLabels: (partnerUUID, userUUID, labels) => {
    let path =
      "/crm/partners/" + partnerUUID + "/members/" + userUUID + "/set-labels/";
    return service.post(path, { labels: labels });
  },

  getPartnerServices: (uuid) => {
    return service.get("/crm/dealers/" + uuid + "/consent-services/");
  },

  setPartnerServices: (uuid, data) => {
    return service.post("/crm/dealers/" + uuid + "/consent-services/", data);
  },

  setBrandServices: (uuid, data) => {
    return service.post("/crm/groups/" + uuid + "/consent-services/", data);
  },

  getBrandServices: (uuid) => {
    return service.get("/crm/groups/" + uuid + "/consent-services/");
  },

  setPrivacyPolicyForPartners: (uuid) => {
    return service.post("/crm/dealers/" + uuid + "/accept-privacy-policy/");
  },

  setTermsAndConditionsForPartners: (uuid) => {
    return service.post("/crm/dealers/" + uuid + "/accept-tac/");
  },

  getPartnerTac: () => {
    return service.get("documents/partner-tac/");
  },

  getPartnerPp: (uuid) => {
    return service.get("documents/privacy-policy/partner/" + uuid + "/");
  },

  // ITEMS
  getItemList: function (params) {
    let url = "/crm/builder/items/";
    let addedString = "";
    if (!params) {
      params = {};
    }
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get(url + addedString);
  },

  getItemNextPage: function (path) {
    return service.get(path);
  },

  deleteItem: function (itemId) {
    var path = "/crm/builder/items/" + itemId + "/";
    return service.delete(path);
  },

  getItem: function (itemId, params, error = true) {
    var path = "/crm/builder/items/" + itemId + "/";
    var addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }

    return service.get(path + addedString, { errorHandle: error });
  },

  createItem: function (data, error = true) {
    var path = "/crm/builder/items/";
    return service.post(path, data, { errorHandle: error });
  },

  updateItem: function (itemsId, data, error = true) {
    var path = "/crm/builder/items/" + itemsId + "/";
    return service.patch(path, data, { errorHandle: error });
  },

  itemCategoriesAutocomplete: function () {
    return service.get("/crm/builder/items/categories/");
  },

  vendorAutocomplete: function (query) {
    return service.get(
      "crm/builder/vendors/?search=" +
        encodeURIComponent(query) +
        "&?autocomplete=true",
    );
  },

  createVendor: function (data) {
    return service.post("crm/builder/vendors/", data);
  },

  itemsAutocomplete: function (query) {
    return service.get(
      "/crm/builder/items/?search=" +
        encodeURIComponent(query) +
        "&?autocomplete=true",
    );
  },

  //DOCUMENTS
  documentsAutocomplete: function (query) {
    return service.get(
      "crm/builder/documents/?search=" +
        encodeURIComponent(query) +
        "&autocomplete=true",
    );
  },
  getDocumentList: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }

    return service.get("/crm/builder/documents/" + addedString);
  },

  getDocumentNextPage: function (path) {
    return service.get(path);
  },

  deleteDocument: function (documentId) {
    var path = "/crm/builder/documents/" + documentId + "/";
    return service.delete(path);
  },

  createDocument: function (data) {
    return service.post("/crm/builder/documents/", data);
  },

  uploadFile: function (documentId, data) {
    var path = "/crm/builder/documents/" + documentId + "/upload-file/";
    return service.post(path, data, {
      headers: {
        "content-type": "multipart/form-data",
      },
    });
  },

  updateDocument: function (documentId, data) {
    var path = "/crm/builder/documents/" + documentId + "/";
    return service.patch(path, data);
  },

  getDocument: function (documentId) {
    var path = "/crm/builder/documents/" + documentId + "/";
    return service.get(path);
  },

  domainsAutocomplete: function (query) {
    return service.get("/crm/dealers/domains/");
  },

  documentCategoriesAutocomplete: function (query) {
    return service.get("/crm/builder/documents/categories/");
  },

  //Messages
  getLastMessageThread: () => {
    var path = "/crm/tickets/last/";
    return service.get(path);
  },

  getSharedTask: function (taskId) {
    var path = "/crm/shared-tasks/" + taskId + "/";
    return service.get(path);
  },

  getTicketList: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("/crm/tickets/" + addedString);
  },
  getTicketNextPage: function (path) {
    return service.get(path);
  },
  getTicket: function (ticketId, error = true) {
    return service.get("/crm/tickets/" + ticketId + "/", {
      errorHandle: error,
    });
  },
  closeTicket: function (ticketId) {
    return service.post("/crm/tickets/" + ticketId + "/close/");
  },
  reopenTicket: function (ticketId) {
    return service.post("/crm/tickets/" + ticketId + "/reopen/");
  },
  assigneeAvailableUsers: function (ticketId) {
    return service.get("/crm/tickets/" + ticketId + "/available-users/");
  },
  assignUsers: function (ticketId, data) {
    return service.post("/crm/tickets/" + ticketId + "/assign-user/", data);
  },
  createTicketNote: function (ticketId, data) {
    return service.post("/crm/tickets/" + ticketId + "/messages/", data);
  },
  updateTicketNote: function (ticketId, messagesId, data) {
    return service.patch(
      "/crm/tickets/" + ticketId + "/messages/" + messagesId + "/",
      data,
    );
  },
  deleteTicketNote: function (ticketId, messagesId, data) {
    return service.delete(
      "/crm/tickets/" + ticketId + "/messages/" + messagesId + "/delete",
      data,
    );
  },

  uploadServiceLogAttachment: function (serviceLogId, data) {
    var path =
      "/crm/service-logs/" + serviceLogId + "/post-resolving-attachment/";
    return service.post(path, data, {
      headers: {
        "content-type": "multipart/form-data",
      },
    });
  },
  resolveServiceLog: function (serviceLogId, data) {
    return service.post(
      "/crm/service-logs/" + serviceLogId + "/post-resolving-note/",
      data,
    );
  },
  updateResolvedServiceLog: function (serviceLogId, data) {
    return service.put(
      "/crm/service-logs/" + serviceLogId + "/post-resolving-note/",
      data,
    );
  },
  getServiceLogHistory: function (boat, params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get(
      "/crm/builder/boats/" + boat + "/service-logs/" + addedString,
    );
  },
  getServiceLogHistoryDetails: function (boat, logUuid) {
    return service.get(
      "/crm/builder/boats/" + boat + "/service-logs/" + logUuid + "/",
    );
  },

  setServiceLog: function (data) {
    return service.post("/crm/upcoming-services/complete/", data);
  },

  getBoatEngineHoursSuggestion: function (boatUuid, params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get(
      "crm/builder/boats/" + boatUuid + "/engine-hours/" + addedString,
    );
  },

  createTaskFromMessage: function (ticketId, messageId) {
    return service.post(
      "crm/tickets/" + ticketId + "/messages/" + messageId + "/create-task/",
    );
  },

  uploadMessageAttachment: function (data) {
    return service.post("/upload-file/", data);
  },

  uploadMessageAttachmentFile: function (path, data) {
    return service.post(path, data, {
      headers: {
        "content-type": "multipart/form-data",
        Authorization: "",
      },
    });
  },
  getAvailableServiceLogReports: function (boatUuid, messageUuid) {
    return service.get(
      "/crm/builder/boats/" +
        boatUuid +
        "/service-logs/available-tasks/?reference_crm_message_uuid=" +
        messageUuid,
    );
  },

  setServiceLogReports: function (boatUuid, data) {
    return service.post(
      "/crm/builder/boats/" + boatUuid + "/service-logs/ticket-flow/",
      data,
    );
  },

  setTicket: (data) => {
    var path = "/crm/tickets/";
    return service.post(path, data);
  },

  //ATTACHMENTS
  uploadAttachments(data) {
    return service.post("/direct-file-upload/", data);
  },

  uploadAttachmentFile: function (path, data) {
    return service.post(path, data, {
      headers: {
        "content-type": "multipart/form-data",
        Authorization: "",
      },
    });
  },

  //PARTNERS
  getCrmPartnerBoats(boatUuid) {
    return service.get("/crm/partners/" + boatUuid + "/boats/");
  },

  // MODELS
  getModelList: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("/crm/builder/models/" + addedString);
  },

  deleteModel: function (modelId) {
    var path = "/crm/builder/models/" + modelId + "/";
    return service.delete(path);
  },

  updateModel: function (modelId, data) {
    var path = "/crm/builder/models/" + modelId + "/";
    return service.put(path, data);
  },

  getModel: function (modelId) {
    var path = "/crm/builder/models/" + modelId + "/";
    return service.get(path);
  },

  createModel: function (data) {
    var path = "/crm/builder/models/";
    return service.post(path, data);
  },

  modelTypeAutocomplete: function () {
    return new Promise((resolve, reject) => {
      resolve({
        data: [
          { uuid: "MB", name: "Motor Boat" },
          { uuid: "SY", name: "Sailing Yacht" },
          { uuid: "MY", name: "Motor Yacht" },
          { uuid: "PB", name: "Power Boat" },
          { uuid: "SC", name: "Sailing Catamaran" },
          { uuid: "PC", name: "Power Catamaran" },
          { uuid: "RB", name: "RIB" },
          { uuid: "HB", name: "House Boat" },
        ],
      });
    });
  },

  //PARTNERS
  getDealerList: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("/crm/dealers/" + addedString);
  },

  deleteDealer: function (partnerId) {
    var path = "/crm/dealers/" + partnerId + "/";
    return service.delete(path);
  },

  updateDealer: function (partnerId, data) {
    var path = "/crm/dealers/" + partnerId + "/";
    return service.put(path, data);
  },

  createDealer: function (data) {
    var path = "/crm/dealers/";
    return service.post(path, data);
  },

  getDealer: function (partnerId, params, error = true) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    var path = "/crm/dealers/" + partnerId + "/" + addedString;
    return service.get(path, {
      errorHandle: error,
    });
  },

  getServiceProviderList: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("/crm/service-providers/" + addedString);
  },

  deleteServiceProvider: function (partnerId) {
    var path = "/crm/service-providers/" + partnerId + "/";
    return service.delete(path);
  },

  updateServiceProvider: function (partnerId, data) {
    var path = "/crm/service-providers/" + partnerId + "/";
    return service.put(path, data);
  },

  createServiceProvider: function (data) {
    var path = "/crm/service-providers/";
    return service.post(path, data);
  },

  getServiceProvider: function (partnerId) {
    var path = "/crm/service-providers/" + partnerId + "/";
    return service.get(path);
  },

  brandsAutocomplete: function (query) {
    return service.get(
      "crm/builder/boats/?search=" +
        encodeURIComponent(query) +
        "&?autocomplete=true",
    );
  },

  inviteDealerUser: (partnerUuid, email, error = true) => {
    var path = "/crm/dealers/" + partnerUuid + "/invite-user/";
    return service.post(path, email, {
      errorHandle: error,
    });
  },

  rejectInviteDealerUser: (uuid) => {
    var path = "/crm/invites/" + uuid + "/reject/";
    return service.post(path);
  },

  deleteUserFromDealers: (uuid, params) => {
    var path = "/crm/dealers/" + uuid + "/remove-user/";
    return service.post(path, params);
  },

  //DEVICES / DATA
  getDevicesList: function (query) {
    return service.get("/crm/devices/?search=" + encodeURIComponent(query));
  },

  getDeviceSensorsList: function (deviceSerial, error = true) {
    return service.get("/crm/devices/" + deviceSerial + "/", {
      errorHandle: error,
    });
  },

  fetchSensorData: function (deviceSerial, sensorId, start, end) {
    return service.get(
      "/crm/devices/" +
        deviceSerial +
        "/sensors/" +
        sensorId +
        "/?start=" +
        encodeURIComponent(start) +
        "&end=" +
        encodeURIComponent(end),
    );
  },
  getDeviceAlarms: function (
    deviceSerial,
    start,
    end,
    error = true,
    suppressSnackbar = false,
  ) {
    return service.get(
      "/crm/devices/" +
        deviceSerial +
        "/alarms/?" +
        "start=" +
        encodeURIComponent(start) +
        "&end=" +
        encodeURIComponent(end),
      {
        errorHandle: error,
        suppressSnackbar: suppressSnackbar,
      },
    );
  },
  getDeviceAlarmNextPage: function (path) {
    return service.get(path);
  },
  getDevicePosition: function (
    deviceSerial,
    start,
    end,
    error = true,
    suppressSnackbar = false,
  ) {
    return service.get(
      "/crm/devices/" +
        deviceSerial +
        "/locations/" +
        "?start=" +
        encodeURIComponent(start) +
        "&end=" +
        encodeURIComponent(end),
      {
        errorHandle: error,
        suppressSnackbar: suppressSnackbar,
      },
    );
  },

  //BOATS
  getBoatList: function (params, signal) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }

    return service.get("/crm/builder/boats/" + addedString, { signal });
  },

  deleteBoat: function (boatsId) {
    var path = "/crm/builder/boats/" + boatsId + "/";
    return service.delete(path);
  },

  updateBoat: function (boatsId, data) {
    var path = "/crm/builder/boats/" + boatsId + "/";
    return service.put(path, data);
  },

  updateBoatAsDealer: function (boatsId, data) {
    var path = "/crm/builder/boats/" + boatsId + "/";
    return service.patch(path, data);
  },

  createBoat: function (data) {
    var path = "/crm/builder/boats/";
    return service.post(path, data);
  },

  getBoat: function (boatsId, error = true) {
    var path = "/crm/builder/boats/" + boatsId + "/";
    return service.get(path, {
      errorHandle: error,
    });
  },

  modelsAutocomplete: function (query) {
    return service.get(
      "crm/builder/models/?search=" +
        encodeURIComponent(query) +
        "&autocomplete=true",
    );
  },

  devicesAutocomplete: function (query) {
    return service.get(
      "crm/devices/?search=" + encodeURIComponent(query) + "&autocomplete=true",
    );
  },

  deviceSearch: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("crm/devices/" + addedString);
  },

  dealersAutocomplete: function (params, error = true) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("crm/dealers/autocomplete/" + addedString, {
      errorHandle: error,
    });
  },

  applyProfile: function (boat_uuid) {
    return service.post("crm/builder/boats/" + boat_uuid + "/apply-profile/");
  },

  setProfileMode: function (boat_uuid, apply_profile_mode) {
    return service.patch(
      "crm/builder/boats/" + boat_uuid + "/profile-mode/",
      { apply_profile_mode },
      // Handle errors locally so the snackbar does not scroll the page to top.
      { errorHandle: false }
    );
  },

  uploadCsvFileEditBoat: function (uuid, data, status) {
    let addedString = "";
    if (status) {
      addedString = "?" + new URLSearchParams("new_logic=true").toString();
    }
    var path =
      "/crm/builder/boats/" + uuid + "/import-items-csv/" + addedString;
    return service.post(path, data, {
      headers: {
        "content-type": "multipart/form-data",
      },
    });
  },
  uploadCsvFileNewBoat: function (uuid, data, status) {
    let addedString = "";
    if (status) {
      addedString = new URLSearchParams("new_logic=true").toString();
    }
    var path =
      "/crm/builder/boats/import-items-csv/?crm_group_uuid=" +
      uuid +
      "&" +
      addedString;
    return service.post(path, data, {
      headers: {
        "content-type": "multipart/form-data",
      },
    });
  },

  transferBoat: function (uuid, data) {
    return service.post(
      "crm/builder/boats/" + uuid + "/customer-handout/",
      data,
    );
  },

  cancelTransferBoat: function (uuid) {
    return service.delete(
      "crm/builder/boats/" + uuid + "/customer-handout/pending/",
    );
  },

  importBoatFromMis: function (data, params = false) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.post("crm/devices/import-from-mis/" + addedString, data);
  },

  resetBoat: function (boatId) {
    var path = "/crm/builder/boats/" + boatId + "/clean/";
    return service.post(path);
  },

  getBoatItems: function (boatUuid, params, error = true) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get(
      "crm/builder/boats/" + boatUuid + "/items/" + addedString,
      {
        errorHandle: error,
      },
    );
  },

  getBoatItemsNextPage: function (path) {
    return service.get(path);
  },

  generateVirtualDevice: function (superGroupUuid) {
    return service.post("crm/devices/create-virtual-device/", {
      crm_group_uuid: superGroupUuid,
    });
  },

  setRetrofitStatus: function (uuid, status) {
    return service.post("crm/builder/boats/" + uuid + "/set-retrofit/", {
      retrofit_status: status,
    });
  },

  boatImpersonate: function (uuid) {
    return service.post("crm/builder/boats/" + uuid + "/customer-care/");
  },

  getPaperConsents: function (uuid) {
    return service.get("crm/builder/boats/" + uuid + "/paper-consents/");
  },

  setPaperConsents: function (uuid, data) {
    return service.post(
      "crm/builder/boats/" + uuid + "/paper-consents/apply-consents/",
      data,
    );
  },

  checkOwnedResources: function (uuid, data) {
    return service.post(
      "crm/builder/boats/" + uuid + "/owned-resources/",
      data,
    );
  },

  approveKYCRequest: function (uuid) {
    return service.post("crm/builder/boats/" + uuid + "/approve-kyc/");
  },

  rejectKYCRequest: function (uuid) {
    return service.post("crm/builder/boats/" + uuid + "/reject-kyc/");
  },

  // DASHBOARDS
  getDashboardList: function () {
    return service.get("/crm/dashboards/");
  },

  getDashboard: function (dashId) {
    return service.get("/crm/dashboards/" + dashId + "/");
  },
  dashboardsAutocomplete: function () {
    var path = "/crm/groups/dashboards/";
    return service.get(path);
  },

  //GROUPS
  getGroupList: function () {
    return service.get("/crm/groups/");
  },
  createGroup: function (groupId, data) {
    var path = "/crm/groups/" + groupId + "/subgroup/";
    return service.post(path, data);
  },
  deleteGroup: function (groupId) {
    var path = "/crm/groups/" + groupId + "/";
    return service.delete(path);
  },
  updateGroup: function (groupId, data) {
    var path = "/crm/groups/" + groupId + "/";
    return service.patch(path, data);
  },
  getGroup: function (groupId) {
    var path = "/crm/groups/" + groupId + "/";
    return service.get(path);
  },

  groupsAutocomplete: function (params) {
    let addedString = "";
    if (params) {
      addedString = "&" + new URLSearchParams(params).toString();
    }
    return service.get(
      "/crm/groups/?autocomplete=true&no_page=true" + addedString,
    );
  },

  profilesAutocomplete: function (groupId) {
    return service.get("/crm/groups/" + groupId + "/available-profiles/");
  },

  //ROLES
  deleteRole: function (groupId, roleId) {
    var path = "/crm/groups/" + groupId + "/roles/" + roleId + "/";
    return service.delete(path);
  },

  updateRole: function (groupId, roleId, data) {
    var path = "/crm/groups/" + groupId + "/roles/" + roleId + "/";
    return service.put(path, data);
  },

  updateUserRole: function (groupId, roleId, data) {
    var path = "/crm/groups/" + groupId + "/roles/" + roleId + "/";
    return service.patch(path, data);
  },

  createRole: function (groupId, data) {
    var path = "/crm/groups/" + groupId + "/roles/";
    return service.post(path, data);
  },

  getRole: (groupId, roleId) => {
    var path = "/crm/groups/" + groupId + "/roles/" + roleId + "/";
    return service.get(path);
  },

  inviteRoleUser: (groupId, roleId, data) => {
    var path = "/crm/groups/" + groupId + "/roles/" + roleId + "/invite-user/";
    return service.post(path, data);
  },

  rejectInviteRoleUser: (uuid) => {
    var path = "/crm/sent-invites/" + uuid + "/cancel/";
    return service.post(path);
  },

  deleteUserFromRole: (groupId, roleId, params) => {
    var path = "/crm/groups/" + groupId + "/roles/" + roleId + "/remove-user/";
    return service.post(path, params);
  },
  //BOOKINGS
  getBookingList: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("/crm/bookings/" + addedString);
  },

  getBooking: function (bookingId) {
    var path = "/crm/bookings/" + bookingId + "/";
    return service.get(path);
  },

  removeBooking: function (bookingId) {
    var path = "/crm/bookings/" + bookingId + "/";
    return service.delete(path);
  },

  getBookingBoats: function () {
    var path = "/crm/bookings/boats/";
    return service.get(path);
  },

  getBookingChecklists: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    var path = "/crm/bookings/checklists/" + addedString;
    return service.get(path);
  },

  createBooking: function (data) {
    var path = "/crm/bookings/";
    return service.post(path, data);
  },
  updateBooking: function (bookingId, data) {
    var path = "/crm/bookings/" + bookingId + "/";
    return service.put(path, data);
  },

  deleteBooking: function (bookingId, data) {
    var path = "/crm/bookings/" + bookingId + "/";
    return service.delete(path);
  },
  //TASKS
  getTaskList: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString() + "&" + status;
    }

    return service.get("/crm/tasks/" + addedString);
  },

  getTask: function (taskId) {
    var path = "/crm/tasks/" + taskId + "/";
    return service.get(path);
  },

  saveSubtask: function (taskId, data) {
    var path = "/crm/tasks/" + taskId + "/";

    return service.patch(path, data);
  },

  saveSharedSubtask: function (taskId, data) {
    var path = "/crm/shared-tasks/" + taskId + "/";
    return service.patch(path, data);
  },

  createTask: function (data) {
    var path = "/crm/tasks/";
    return service.post(path, data);
  },
  updateTask: function (taskId, data) {
    var path = "/crm/tasks/" + taskId + "/";
    return service.put(path, data);
  },

  reopenTask: function (taskId, data) {
    var path = "/crm/tasks/" + taskId + "/";
    return service.patch(path, data);
  },

  updateSharedTask: function (taskId, data) {
    var path = "/crm/shared-tasks/" + taskId + "/";
    return service.patch(path, data);
  },

  finishTask: function (taskId) {
    var path = "/crm/tasks/" + taskId + "/finish/";
    return service.post(path);
  },
  finishSharedTask: function (taskId) {
    var path = "/crm/shared-tasks/" + taskId + "/finish/";
    return service.post(path);
  },
  deleteTask: function (taskId, data) {
    var path = "/crm/tasks/" + taskId + "/";
    return service.delete(path);
  },
  taskAssigneeAutocomplete: function (taskId, params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("/crm/partners/" + taskId + "/members/");
  },
  taskBoatAutocomplete: function (taskId, params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("/crm/dealers/" + taskId + "/boats/");
  },
  taskAttachment: function (taskId, data) {
    var path = "/crm/tasks/" + taskId + "/post-attachment/";
    return service.post(path, data, {
      headers: {
        "content-type": "multipart/form-data",
      },
    });
  },
  sharedTaskAttachment: function (taskId, data) {
    var path = "/crm/shared-tasks/" + taskId + "/post-attachment/";
    return service.post(path, data, {
      headers: {
        "content-type": "multipart/form-data",
      },
    });
  },

  //SERVICES
  getServiceList: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("crm/upcoming-services/" + addedString);
  },

  getService(serviceUuid) {
    return service.get("crm/upcoming-services/" + serviceUuid + "/");
  },

  boatsAutocomplete: function (params, maintenanceContent) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    if (maintenanceContent) {
      addedString += "&include_maintenance_consent=true";
    }
    return service.get("crm/builder/boats/" + addedString);
  },

  maintenanceBoatsOverviewAutocomplete: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("crm/builder/boats/maintenance-overview/" + addedString);
  },

  //TEMPLATES
  getTemplateList: function (params) {
    let addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get("/crm/tasks/templates/" + addedString);
  },

  getTemplateNextPage: function (path) {
    return service.get(path);
  },

  createTemplate: function (data) {
    var path = "/crm/tasks/templates/";
    return service.post(path, data);
  },

  updateTemplate: function (templateId, data) {
    var path = "/crm/tasks/templates/" + templateId + "/";
    return service.put(path, data);
  },

  getTemlate: function (templateId) {
    var path = "/crm/tasks/templates/" + templateId + "/";
    return service.get(path);
  },

  deleteTemplate: function (templateId) {
    var path = "/crm/tasks/templates/" + templateId + "/";
    return service.delete(path);
  },

  sharedTemplateAttachment: function (templateUuid, checklistUuid, data) {
    var path =
      "crm/tasks/templates/" +
      templateUuid +
      "/post-attachment/?checklist_uuid=" +
      checklistUuid;
    return service.post(path, data, {
      headers: {
        "content-type": "multipart/form-data",
      },
    });
  },

  //NEWSFEED
  getNewsFeedList: function (params) {
    var path = "/crm/newsfeed/";
    var addedString = "";
    if (params) {
      addedString = "?" + new URLSearchParams(params).toString();
    }
    return service.get(path + addedString);
  },
  deleteNewsFeed: function (newsFeedUuid) {
    var path = "/crm/newsfeed/" + newsFeedUuid + "/";
    return service.delete(path);
  },
  getNewsFeedNextPage: function (path) {
    return service.get(path);
  },
  getNewsFeed: function (newsFeedId) {
    var path = "/crm/newsfeed/" + newsFeedId + "/";
    return service.get(path);
  },
  updateNewsFeed: function (newsFeedId, data) {
    var path = "/crm/newsfeed/" + newsFeedId + "/";
    return service.patch(path, data);
  },
  createNewsFeed: function (data) {
    var path = "/crm/newsfeed/";
    return service.post(path, data);
  },

  getEstimateAudience: function (data) {
    var path = "/crm/newsfeed/estimate-audience/?" + data;
    return service.get(path);
  },
  //NOTIFICATION CENTER
  getNotifications: function (data) {
    var path = "/crm/notifications/";
    return service.get(path);
  },
  setSeenNotifications: function (data) {
    var path = "/crm/notifications/mark-seen/";
    return service.post(path, data);
  },
  getNotificationsNextPage: function (path) {
    return service.get(path);
  },
  //DIAGNOSTICS REPORTS
  getLastReport: async (boatId) => {
    return service.get(`boats/${boatId}/installation-reports/last/`);
  },
  getReports: async (boatId) => {
    return service.get(`crm/builder/boats/${boatId}/installation-reports/`);
  },
  //SETTINGS
  updateProfile: function (data) {
    const path = "users/me/";
    return service.post(path, data);
  },

  //BILLING
  getBillingIframeToken: function (dealerUuid) {
    const path = "crm/partners/create-jwt/";
    return service.post(path, { crm_partner_uuid: dealerUuid });
  },
};
